import { ActionIcon, Group, Stack, Switch, Tooltip } from '@mantine/core';
import { Link } from '@tanstack/react-router';
import { type ColumnDef } from '@tanstack/react-table';
import { LuPencil, LuSettings2, LuTrash2 } from 'react-icons/lu';
import { type SchemaListItem } from '@morph-mapper/types';
import { formatDate } from '../../../utils';
import {
  StyledActionLinkContainer,
  StyledDescriptionText,
  StyledMutedMonospaceText,
  StyledNameText,
  StyledRightAlignedCell,
  StyledStatusSwitchWrap,
  StyledUpdatedAtText,
} from './styles';

type GetSchemaListColumnsOptions = {
  archivePendingSchemaId?: string;
  onArchive: (schema: SchemaListItem) => void;
  onToggleStatus: (schema: SchemaListItem) => void;
};

export const getSchemaListColumns = ({
  archivePendingSchemaId,
  onArchive,
  onToggleStatus,
}: GetSchemaListColumnsOptions): Array<ColumnDef<SchemaListItem>> => [
  {
    accessorKey: 'name',
    cell: ({ row }) => (
      <Stack spacing={4}>
        <StyledNameText>{row.original.name}</StyledNameText>
        {row.original.description ? (
          <StyledDescriptionText>
            {row.original.description}
          </StyledDescriptionText>
        ) : null}
      </Stack>
    ),
    header: 'Name',
  },
  {
    accessorKey: 'domain',
    cell: ({ getValue }) => (
      <StyledMutedMonospaceText>{getValue<string>()}</StyledMutedMonospaceText>
    ),
    header: 'Domain',
  },
  {
    accessorKey: 'type',
    cell: ({ getValue }) => (
      <StyledMutedMonospaceText>{getValue<string>()}</StyledMutedMonospaceText>
    ),
    header: 'Type',
  },
  {
    accessorKey: 'variant',
    cell: ({ getValue }) => (
      <StyledMutedMonospaceText>{getValue<string>()}</StyledMutedMonospaceText>
    ),
    header: 'Variant',
  },
  {
    accessorKey: 'status',
    cell: ({ row }) => {
      if (row.original.status === 'archived') {
        return <>-</>;
      }

      return (
        <StyledStatusSwitchWrap>
          <Switch
            checked={row.original.status === 'active'}
            color="teal"
            offLabel="I"
            onLabel="A"
            radius={0}
            size="sm"
            onClick={() => void onToggleStatus(row.original)}
          />
        </StyledStatusSwitchWrap>
      );
    },
    header: 'Status',
  },
  {
    accessorFn: (row) => row.operatorId.join(', '),
    cell: ({ row }) => (
      <StyledMutedMonospaceText>
        {row.original.operatorId.length > 0
          ? row.original.operatorId.join(', ')
          : 'None'}
      </StyledMutedMonospaceText>
    ),
    header: 'Operator IDs',
    id: 'operatorId',
  },
  {
    accessorKey: 'updatedAt',
    cell: ({ getValue }) => (
      <StyledRightAlignedCell>
        <StyledUpdatedAtText>
          {formatDate(getValue<string | Date | undefined>())}
        </StyledUpdatedAtText>
      </StyledRightAlignedCell>
    ),
    header: 'Updated',
  },
  {
    cell: ({ row }) => {
      return (
        <Group spacing={4} noWrap className="actions-cell">
          <Tooltip label="Edit schema" position="top" withArrow>
            <StyledActionLinkContainer>
              <Link
                to="/schemas/$schemaId"
                params={{ schemaId: row.original.id }}
              >
                <ActionIcon
                  aria-label="Edit schema"
                  color="gray"
                  size="sm"
                  variant="subtle"
                >
                  <LuPencil size={16} />
                </ActionIcon>
              </Link>
            </StyledActionLinkContainer>
          </Tooltip>
          <Tooltip label="Schema settings" position="top" withArrow>
            <StyledActionLinkContainer>
              <Link
                to="/schemas/$schemaId/settings"
                params={{ schemaId: row.original.id }}
              >
                <ActionIcon
                  aria-label="Edit schema settings"
                  color="gray"
                  size="sm"
                  variant="subtle"
                >
                  <LuSettings2 size={16} />
                </ActionIcon>
              </Link>
            </StyledActionLinkContainer>
          </Tooltip>
          <Tooltip label="Archive schema" position="top" withArrow>
            <ActionIcon
              aria-label="Delete schema"
              color="gray"
              loading={archivePendingSchemaId === row.original.id}
              onClick={() => onArchive(row.original)}
              size="sm"
              variant="subtle"
            >
              <LuTrash2 size={16} />
            </ActionIcon>
          </Tooltip>
        </Group>
      );
    },
    enableSorting: false,
    header: '',
    id: 'actions',
  },
];
